/**
 * Lesson content with an offline cache of the most recently opened lessons. The cache is
 * parsed from localStorage once and then served from memory; opening a cached lesson is
 * synchronous (`peekLesson`), so screens render it without a loading frame.
 */
import { fetchLessonContent, type LessonContent } from './api.ts';
import { readJson, writeJson } from './storage.ts';

const CACHE_KEY = 'soile.lessons.v2';
const CACHE_SIZE = 8;

type Cache = LessonContent[];

let memory: Cache | null = null;
const inflight = new Map<string, Promise<LessonContent>>();

function readCache(): Cache {
  memory ??= readJson<Cache>(CACHE_KEY) ?? [];
  return memory;
}

function remember(entry: LessonContent): void {
  const rest = readCache().filter((c) => c.id !== entry.id);
  let next = [entry, ...rest].slice(0, CACHE_SIZE);
  memory = next;
  // On quota errors drop the oldest entries until the write fits (memory keeps them all).
  while (!writeJson(CACHE_KEY, next) && next.length > 1) next = next.slice(0, -1);
}

export function cachedLesson(id: string): LessonContent | null {
  return readCache().find((c) => c.id === id) ?? null;
}

/** The cached lesson when it is known to be current (revision matches), else null. */
export function peekLesson(id: string, knownRevision: string | null): LessonContent | null {
  const cached = cachedLesson(id);
  return cached && knownRevision !== null && cached.revision === knownRevision ? cached : null;
}

/**
 * Returns the lesson, preferring the cache when its revision matches `knownRevision`
 * (from the lesson list), and falling back to the cache when offline. Concurrent calls for
 * one lesson share a request.
 */
export function loadLesson(id: string, knownRevision: string | null): Promise<LessonContent> {
  const current = peekLesson(id, knownRevision);
  if (current) return Promise.resolve(current);
  let request = inflight.get(id);
  if (!request) {
    request = fetchLessonContent(id)
      .then((fresh) => {
        remember(fresh);
        return fresh;
      })
      .catch((error: unknown) => {
        const cached = cachedLesson(id);
        if (cached) return cached;
        throw error;
      })
      .finally(() => inflight.delete(id));
    inflight.set(id, request);
  }
  return request;
}

/** Downloads a lesson ahead of time; failures are ignored (opening it retries). */
export function prefetchLesson(id: string, knownRevision: string | null): void {
  loadLesson(id, knownRevision).catch(() => {});
}
